// The Floor's New PCs tray: a PC that started from the venue's boot server and is not a station yet (`GET …/boot/new-pcs`,
// `boot:view`, the Clavian Boot add-on). One card for each, newest first, drawn above the Floor in every view. A card leads
// with what tells the PC apart to staff — its graphics card, processor and network card in words, and when it first came —
// with its network address and motherboard under a small Details toggle. Where its motherboard is not the one the image it
// would start was made on, the card says so. Empty, or for a member without `boot:view` or an organisation without the
// add-on, the tray is not drawn at all. Once a member hides it, it is one line ("2 new PCs — Set up") until a PC they have
// not seen arrives; that is remembered on this device, per member and venue.
//
// The actions on a card:
//   - **Identify** (`boot:manage`): the PC shows the number it would be given, large, on its own screen for a minute, so staff
//     can tell which PC in the room it is. It goes through the boot server; a PC that is off or still starting cannot show it,
//     and the card says so quietly.
//   - **Make it station N** (`boot:manage` and `stations:manage`): a centred dialog — the number (the next free one), the
//     class and the zone, with a name under More options — which makes the station and links its boot PC in one request.
//     With Clavian Stations the Clavian Station app on that PC pairs to it by itself and the tile reads Pairing until it has;
//     Boot only, the tile is on the floor at once.
//   - **Remove…** in the card's menu (`boot:manage`): a small confirm, then `POST …/boot/pcs/{id}/remove` — it leaves the
//     list and stops counting against the venue's Clavian Boot PCs.
//
// Not built yet: dragging a card onto the floor plan (Room view, Arrange) to make the station *and* place it on a square. The
// request has no `placement` yet, so a station made here is unplaced, like any other, until it is arranged.

import type { BootNewPc, FloorStationState } from '@clavian/api-client';
import { MAX_STATION_NUMBER } from '@clavian/contracts';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, Dialog, FormSection, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';

import type { Access } from '../api/queries';
import { PROBLEM, problemCode, problemStatus } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { useNow } from '../floor/live';
import { useStationClasses, useZones } from '../stations/queries';
import { Notice, RowActions } from '../ui';
import { ConfirmDialog } from './ConfirmDialog';
import { hardwareLine, motherboardLabel, motherboardMismatch } from './hardware';
import { useBootNewPcs, useIdentifyBootPc, useMakeBootPcStation, useRemoveBootPc, type BootScope } from './queries';

import './new-pcs.css';

const BOOT_VIEW = 'boot:view';
const BOOT_MANAGE = 'boot:manage';
const STATIONS_MANAGE = 'stations:manage';

/**
 * The number a new PC would be given: one past the highest station number the venue holds (a removed station is not on the
 * floor, so its number is free). The highest plus one is simpler than the lowest gap and never reuses a number a player may
 * still remember for another PC.
 */
export function nextStationNumber(stations: readonly Pick<FloorStationState, 'number'>[]): number {
  return stations.reduce((highest, station) => Math.max(highest, station.number), 0) + 1;
}

/** Where a member's "hidden" New PCs are remembered, on this device: the PCs the tray held when they hid it. */
function seenKey(memberId: string | null, venueId: string): string {
  return `clavian.newPcs.seen.${memberId ?? 'anyone'}.${venueId}`;
}

function readSeen(key: string): ReadonlySet<string> | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((item): item is string => typeof item === 'string')) : null;
  } catch {
    return null;
  }
}

function writeSeen(key: string, ids: readonly string[] | null): void {
  try {
    if (ids === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // A private window: the tray stays as it is until the page closes.
  }
}

/**
 * Whether the tray is drawn as one line: the member hid it, and every PC in it now is one they had seen then. A new PC
 * opens it again by itself, so one never waits unnoticed.
 */
export function trayCollapsed(seen: ReadonlySet<string> | null, ids: readonly string[]): boolean {
  return seen !== null && ids.every((id) => seen.has(id));
}

export function NewPcsTray({
  scope,
  access,
  stations,
}: {
  readonly scope: BootScope;
  readonly access: Access;
  /** The venue's stations on the floor now: the next free number follows them. */
  readonly stations: readonly Pick<FloorStationState, 'number'>[];
}): ReactNode {
  const { t } = useTranslation('console');
  const ready = access.status === 'ready';
  const pcs = useBootNewPcs(scope, ready && access.can(BOOT_VIEW));
  const [making, setMaking] = useState<{ readonly pc: BootNewPc; readonly number: number } | null>(null);
  const [removing, setRemoving] = useState<BootNewPc | null>(null);
  const key = seenKey(access.memberId, scope.venueId);
  const [seen, setSeen] = useState<ReadonlySet<string> | null>(() => readSeen(key));
  const list = useMemo(() => pcs.data ?? [], [pcs.data]);
  const remove = useRemoveBootPc(scope);
  const { show } = useToast();
  if (!ready || !access.can(BOOT_VIEW) || list.length === 0) return null;

  const canIdentify = access.can(BOOT_MANAGE);
  const canMake = access.can(BOOT_MANAGE) && access.can(STATIONS_MANAGE);
  const canRemove = access.can(BOOT_MANAGE);
  const both = access.products.stations && access.products.boot;
  const first = nextStationNumber(stations);
  const ids = list.map((pc) => pc.bootPcId);
  const collapsed = trayCollapsed(seen, ids);
  const hide = () => {
    writeSeen(key, ids);
    setSeen(new Set(ids));
  };
  const expand = () => {
    writeSeen(key, null);
    setSeen(null);
  };

  if (collapsed) {
    return (
      <section className="console-newpcs console-newpcs--collapsed" aria-label={t('boot.newPcs.title')}>
        <p className="console-newpcs__summary">
          <span className="console-newpcs__title">{t('boot.newPcs.collapsed', { count: list.length })}</span>
          <span aria-hidden="true"> — </span>
          <Button variant="secondary" size="sm" aria-expanded={false} onClick={expand}>
            {t('boot.newPcs.setUp')}
          </Button>
        </p>
      </section>
    );
  }

  return (
    <section className="console-newpcs" aria-labelledby="console-newpcs-title">
      <header className="console-newpcs__head">
        <h2 id="console-newpcs-title" className="console-newpcs__title">
          {t('boot.newPcs.title')}
        </h2>
        <p className="console-newpcs__subtitle">{both ? t('boot.newPcs.subtitleBoth') : t('boot.newPcs.subtitle')}</p>
        <Button variant="ghost" size="sm" className="console-newpcs__hide" aria-expanded onClick={hide}>
          {t('boot.newPcs.hide')}
        </Button>
        {canIdentify ? <p className="console-newpcs__hint">{t('boot.newPcs.identifyHint')}</p> : null}
      </header>
      <ul className="console-newpcs__list">
        {list.map((pc, index) => (
          <NewPcCard
            key={pc.bootPcId}
            scope={scope}
            pc={pc}
            // Each card is offered its own number, so two PCs identified together do not show the same one.
            number={first + index}
            canIdentify={canIdentify}
            canMake={canMake}
            canRemove={canRemove}
            onMake={(number) => setMaking({ pc, number })}
            onRemove={() => setRemoving(pc)}
          />
        ))}
      </ul>
      {making === null ? null : (
        <MakeStationDialog key={making.pc.bootPcId} scope={scope} pc={making.pc} number={making.number} stationsOffered={access.products.stations} onClose={() => setMaking(null)} />
      )}
      <ConfirmDialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={t('boot.newPcs.removeTitle')}
        description={t('boot.newPcs.removeBody')}
        confirmLabel={t('boot.newPcs.removeConfirm')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        failedText={t('boot.newPcs.removeFailed')}
        onConfirm={async () => {
          if (removing === null) return;
          await remove.mutateAsync(removing.bootPcId);
          show({ tone: 'ok', title: t('boot.newPcs.removed') });
        }}
      >
        {removing === null ? null : <p className="console-muted">{hardwareLine(removing, t) || removing.mac}</p>}
      </ConfirmDialog>
    </section>
  );
}

function NewPcCard({
  scope,
  pc,
  number,
  canIdentify,
  canMake,
  canRemove,
  onMake,
  onRemove,
}: {
  readonly scope: BootScope;
  readonly pc: BootNewPc;
  readonly number: number;
  readonly canIdentify: boolean;
  readonly canMake: boolean;
  readonly canRemove: boolean;
  readonly onMake: (number: number) => void;
  readonly onRemove: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const identify = useIdentifyBootPc(scope);
  const [shown, setShown] = useState<{ readonly label: string; readonly until: string } | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const now = useNow(5_000);

  // What the PC is showing: what we just sent, or — from another screen, or after a reload — what the api says is on.
  const until = shown?.until ?? pc.identifyUntil;
  const showing = until !== null && Date.parse(until) > now;
  const label = shown?.label ?? String(number);

  const hardware = hardwareLine(pc, t);
  // An image works on the motherboard model it was made on (ADR-0065 decision 8): the card says so before it is made a station.
  const boards = motherboardMismatch(pc.motherboard, pc.imageMotherboard);
  const when = [
    t('boot.newPcs.firstSeen', { when: format.relative(pc.firstSeenAt) }),
    pc.lastStartAt === null ? t('boot.newPcs.neverStarted') : t('boot.newPcs.lastStart', { when: format.relative(pc.lastStartAt) }),
  ].join(' · ');

  function send(): void {
    setFailure(null);
    identify.mutate(
      { bootPcId: pc.bootPcId, label: String(number) },
      {
        onSuccess: (sent) => setShown({ label: sent.label, until: sent.until }),
        // `409`: the boot server is not online, so nothing can reach the PC.
        onError: (error) => setFailure(problemStatus(error) === 409 ? t('boot.newPcs.identifyOffline') : t('boot.newPcs.identifyFailed')),
      },
    );
  }

  return (
    <li className="console-newpcs__card" aria-label={t('boot.newPcs.cardAria', { mac: pc.mac })}>
      <div className="console-newpcs__body">
        <p className="console-newpcs__hardware">{hardware === '' ? t('boot.newPcs.noHardware') : hardware}</p>
        <p className="console-newpcs__when">{when}</p>
        {boards === null ? null : (
          <Notice tone="warn" className="console-newpcs__board">
            {t('boot.floor.motherboard.mismatch', boards)}
          </Notice>
        )}
        <details className="console-newpcs__details">
          <summary>{t('boot.newPcs.details')}</summary>
          <p className="console-newpcs__mac">
            {t('boot.newPcs.mac')}{' '}
            <span className="console-code">{pc.mac}</span>
          </p>
          {pc.motherboard == null ? null : <p className="console-newpcs__mac">{t('boot.newPcs.motherboard', { name: motherboardLabel(pc.motherboard) })}</p>}
        </details>
        {showing ? (
          <p className="console-newpcs__showing" role="status">
            {t('boot.newPcs.showing', { label })}
          </p>
        ) : null}
        {failure === null ? null : (
          <p className="console-newpcs__failure" role="alert">
            {failure}
          </p>
        )}
      </div>
      <div className="console-newpcs__actions">
        {canIdentify ? (
          <Button variant="ghost" size="sm" busy={identify.isPending} onClick={send}>
            {t('boot.newPcs.identify')}
          </Button>
        ) : null}
        {canMake ? (
          <Button variant="secondary" size="sm" className="console-newpcs__make" aria-haspopup="dialog" onClick={() => onMake(number)}>
            {t('boot.newPcs.make', { number })}
          </Button>
        ) : null}
        {canRemove ? <RowActions name={hardware === '' ? pc.mac : hardware} items={[{ id: 'remove', label: t('boot.newPcs.remove'), tone: 'danger', onSelect: onRemove }]} /> : null}
      </div>
    </li>
  );
}

interface FieldErrors {
  number?: string;
  classId?: string;
  form?: string;
}

/** Make it station: the number, class and zone — a name under More options — in one centred dialog. */
function MakeStationDialog({
  scope,
  pc,
  number: suggested,
  stationsOffered,
  onClose,
}: {
  readonly scope: BootScope;
  readonly pc: BootNewPc;
  readonly number: number;
  /** The venue has Clavian Stations: the Clavian Station app on the PC pairs by itself. */
  readonly stationsOffered: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const classes = useStationClasses(scope.organizationId, scope.venueId);
  const zones = useZones(scope.organizationId, scope.venueId);
  const make = useMakeBootPcStation(scope);
  const [number, setNumber] = useState(String(suggested));
  const [classChoice, setClassChoice] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [name, setName] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  // One Idempotency-Key for one body: a retry of the same form is the same request; a changed form is a new one.
  const attempt = useRef<{ readonly signature: string; readonly key: string } | null>(null);

  const classOptions = (classes.data ?? []).map((klass) => ({ value: klass.id, label: klass.name }));
  // The first class is chosen until the person picks another.
  const classId = classChoice !== '' ? classChoice : (classOptions[0]?.value ?? '');
  const zoneOptions = [{ value: '', label: t('boot.makeStation.zoneNone') }, ...(zones.data ?? []).map((zone) => ({ value: zone.id, label: zone.name }))];
  const shownNumber = number.trim() === '' ? '' : number.trim();

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    const value = Number(number);
    if (number.trim() === '' || !Number.isInteger(value) || value < 1 || value > MAX_STATION_NUMBER) next.number = t('boot.makeStation.numberRequired', { max: MAX_STATION_NUMBER });
    if (classId === '') next.classId = t('boot.makeStation.classRequired');
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    const trimmed = name.trim();
    const body = { number: value, classId, ...(zoneId === '' ? {} : { zoneId }), ...(trimmed === '' ? {} : { name: trimmed }) };
    const signature = JSON.stringify(body);
    if (attempt.current?.signature !== signature) attempt.current = { signature, key: crypto.randomUUID() };
    make.mutate(
      { bootPcId: pc.bootPcId, body, idempotencyKey: attempt.current.key },
      {
        onSuccess: (made) => {
          onClose();
          show({ tone: 'ok', title: t(stationsOffered ? 'boot.makeStation.madePairing' : 'boot.makeStation.made', { number: made.station.number }) });
        },
        onError: (error) => {
          const code = problemCode(error);
          if (code === PROBLEM.conflict) setErrors({ number: t('boot.makeStation.numberTaken', { number: value }) });
          else if (code === PROBLEM.notFound) setErrors({ form: t('boot.makeStation.notFound') });
          else setErrors({ form: problem.text(error, { fallback: t('boot.makeStation.failed') }) });
        },
      },
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      size="md"
      dirty="auto"
      eyebrow={pc.mac}
      title={t('boot.makeStation.title')}
      description={stationsOffered ? t('boot.makeStation.pairs') : t('boot.makeStation.atOnce')}
    >
      <form className="console-form" aria-label={t('boot.makeStation.title')} noValidate onSubmit={submit}>
        {errors.form === undefined ? null : (
          <Notice tone="danger" role="alert">
            {errors.form}
          </Notice>
        )}
        {classes.data !== undefined && classOptions.length === 0 ? <Notice tone="warn">{t('boot.makeStation.noClasses')}</Notice> : null}
        <FormSection
          more={
            <TextField label={t('boot.makeStation.name')} description={t('boot.makeStation.nameHint')} showOptional autoComplete="off" value={name} onChange={(event) => setName(event.target.value)} />
          }
          moreLabel={t('boot.makeStation.more')}
        >
          <div className="console-form__row">
            <TextField
              label={t('boot.makeStation.number')}
              description={t('boot.makeStation.numberHint')}
              required
              numeric
              inputMode="numeric"
              value={number}
              onChange={(event) => setNumber(event.target.value)}
              error={errors.number}
            />
            <SelectField
              label={t('boot.makeStation.class')}
              required
              placeholder={t('boot.makeStation.chooseClass')}
              options={classOptions}
              value={classId}
              onValueChange={setClassChoice}
              error={errors.classId}
            />
          </div>
          <SelectField label={t('boot.makeStation.zone')} showOptional options={zoneOptions} value={zoneId} onValueChange={setZoneId} />
        </FormSection>
        <div className="console-form__actions console-form__actions--end">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" busy={make.isPending}>
            {t('boot.makeStation.submit', { number: shownNumber })}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
